import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

/**
 * SearchableAccountSelect - A reusable searchable dropdown for Chart of Accounts
 * 
 * Features:
 * - Typeahead filtering on account code and name
 * - Keyboard navigation (Arrow keys, Enter, Escape)
 * - Groups accounts by type for easier scanning
 * - Compact display with account code prefix
 * 
 * @param {Object} props
 * @param {Array} props.accounts - Array of account objects with { account_id, code, name, type, is_active }
 * @param {string} props.value - Currently selected account_id
 * @param {function} props.onValueChange - Callback when selection changes
 * @param {string} props.placeholder - Placeholder text when no selection
 * @param {boolean} props.disabled - Disable the select
 * @param {string} props.className - Additional CSS classes for trigger button
 * @param {string} props.testId - data-testid for testing
 * @param {Array} props.filterTypes - Optional array of account types to show (e.g., ['expense', 'asset'])
 */
export function SearchableAccountSelect({
  accounts = [],
  value,
  onValueChange,
  placeholder = "Select account...",
  disabled = false,
  className,
  testId,
  filterTypes = null,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef(null);

  // Filter accounts by type if filterTypes provided
  // Include accounts where is_active is true or undefined (default to active)
  const filteredByType = filterTypes 
    ? accounts.filter(a => (a.is_active !== false) && filterTypes.includes(a.type?.toLowerCase()))
    : accounts.filter(a => a.is_active !== false);

  // Client-side search filtering on code and name
  const filteredAccounts = search
    ? filteredByType.filter(account => {
        const searchLower = search.toLowerCase();
        const codeMatch = account.code?.toLowerCase().includes(searchLower);
        const nameMatch = account.name?.toLowerCase().includes(searchLower);
        // Also match on subtype for bank accounts
        const subtypeMatch = account.subtype?.toLowerCase().includes(searchLower);
        return codeMatch || nameMatch || subtypeMatch;
      })
    : filteredByType;

  // Group accounts by type for organized display
  const groupedAccounts = filteredAccounts.reduce((groups, account) => {
    const type = account.type || 'Other';
    if (!groups[type]) {
      groups[type] = [];
    }
    groups[type].push(account);
    return groups;
  }, {});

  // Order of account types for display
  const typeOrder = ['expense', 'income', 'asset', 'liability', 'equity', 'Other'];
  const sortedTypes = Object.keys(groupedAccounts).sort((a, b) => {
    const aIndex = typeOrder.indexOf(a.toLowerCase());
    const bIndex = typeOrder.indexOf(b.toLowerCase());
    return (aIndex === -1 ? 99 : aIndex) - (bIndex === -1 ? 99 : bIndex);
  });

  // Find selected account for display
  const selectedAccount = accounts.find(a => a.account_id === value);

  // Format type name for display
  const formatTypeName = (type) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Handle selection
  const handleSelect = (accountId) => {
    onValueChange(accountId);
    setOpen(false);
    setSearch('');
  };

  // Focus input when popover opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Handle keyboard escape
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setSearch('');
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            className
          )}
          data-testid={testId}
        >
          {selectedAccount ? (
            <span className="truncate">
              <span className="font-mono text-[10px] text-muted-foreground mr-1">
                {selectedAccount.code}
              </span>
              {selectedAccount.name}
            </span>
          ) : (
            <span>{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start" onKeyDown={handleKeyDown}>
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by code or name..."
              className="flex h-9 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
          <CommandList className="max-h-[280px]">
            {filteredAccounts.length === 0 ? (
              <CommandEmpty>No accounts found.</CommandEmpty>
            ) : (
              sortedTypes.map((type) => (
                <CommandGroup key={type} heading={formatTypeName(type)}>
                  {groupedAccounts[type].map((account) => (
                    <CommandItem
                      key={account.account_id}
                      value={account.account_id}
                      onSelect={() => handleSelect(account.account_id)}
                      className="cursor-pointer"
                      data-testid={testId ? `${testId}-option-${account.code}` : undefined}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === account.account_id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className="font-mono text-[10px] text-muted-foreground mr-2 w-12">
                        {account.code}
                      </span>
                      <span className="truncate">{account.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default SearchableAccountSelect;
